import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import { compact, get, isEqual, memoize, range, sortedUniq, uniqBy, uniqueId, values } from 'lodash';
import moment from 'moment';
import { default as React, ReactNode } from 'react';
import { PixelCrop } from 'react-image-crop';

import { UploadImageResultFormatsApi, UploadImageResultSmartCenter } from '../../api/RestApiManager';
import { ActionsMenuGroup } from '../../components/ActionsMenu/ActionsMenu';
import { CropFormats } from '../../components/BannerImageCrop/BannerImageCrop';
import { PreviewContentPromotionBannerProps } from '../../components/PreviewContentPromotionBanner/PreviewContentPromotionBanner';
import { Shape2Icon } from '../../components/Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../../components/StyledLink/StyledLink';
import { DEFAULT_SITELINKS_COUNT } from '../../constants';
import { mapBannersSiteLinksToApiFormat } from '../../mappers/banners-edit/sitelinks-mappers';
import { getEditableBannerTypes } from '../../selectors/bannerEdit/banner';
import { selectVisitingCardEditorValue } from '../../selectors/visitingCardEditor/visitingCardEditor';
import {
    BannerId,
    BannerPrice,
    BannersEditApiData,
    BannersEditApiDataCopy,
    CityWithMetroStations,
    Day,
    ImageCreativeEditorActionTypes,
    ImageOrCreativeUploadingMode,
    MessengerType,
    PhoneNumberData,
    PrefixPrice,
    SiteLinkData,
    VcardData,
    WorkPeriodInfo
} from '../../typings/banners';
import {
    BannersIdsGroupsByOpenState,
    BannersIdsGroupsByOpenStateForVisitingCard,
    BannerSiteLinksEditorData,
    ContentPromotionBannerType,
    DraftBanner,
    EditBannersState,
    EditedBanner,
    EditedBannerBase,
    EditedBannerImage,
    EditedBannerPrice,
    EditedBannerSiteLinks,
    EditedBannerTurbolanding,
    EditedBannerTypeCpmOutdoor,
    EditedBannerTypeMcBanner,
    EditedBannerTypePerformance,
    EditedContentPromotionBanner,
    EditedCpmBanner,
    EditorBannerPriceData,
    EditorBannerPriceId,
    getEditedBannerWithFullContacts,
    getEditedBannerWithVcard,
    ProtocolValue
} from '../../typings/bannersEditState';
import {
    AccessRights,
    EditorPlaceId,
    LinkTarget,
    ValidationErrorData,
    ValidationResultItem
} from '../../typings/common';
import { CreativeState } from '../../typings/creative';
import {
    AdditionsFlags,
    AdvType,
    AdvTypeClient,
    Banner,
    BannerImageFormat,
    BannerImageSmartCenter,
    BannerImageType,
    BannerPreviewData,
    BannerStatus,
    CampaignId,
    CampaignType,
    ContactsEditorType,
    PreviewType
} from '../../typings/domain';
import {
    adImageFormatsFragment,
    adImageSmartCentersFragment,
    adsRowFragmentForEdit_GdTextAd_bannerPrice,
    adsRowFragmentForEdit_GdTextAd_sitelinks,
    CurrencyCode,
    GdAddSitelinkInput,
    GdAdGroupPrimaryStatus,
    GdAdPriceCurrency,
    GdAdPriceCurrencyInput,
    GdAdPriceInput,
    GdAdPricePrefixInput,
    GdAdPrimaryStatus,
    GdAdResources,
    GdBannerMeasurerSystem,
    GdCampaignPlatform,
    GdCampaignPrimaryStatus,
    OperatorFeaturesFragment
} from '../../typings/graphql-grid';
import {
    BannersEditMassActionGroup,
    BannersEditMassActionItem,
    BannersEditMassActionItems,
    BannersEditMassActionItemType,
    GroupId
} from '../../typings/groups';
import { PcodeAdsImage } from '../../typings/pcode';
import { ClientFeatures, State } from '../../typings/state';
import { VideoState } from '../../typings/video';
import { mapErrorCodeToMessage } from '../BannerEditErrors/BannerEditErrors';
import { getIconNameForCampaignType } from '../CampaignHelpers/CampaignHelpers';
import { ApiMethodExecutingError } from '../Errors';
import { UrlHelpers } from '../UrlHelpers';

import * as keyset from './BannerHelpers.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

export enum HrefValidationStatuses {
    CANNOT_BE_NULL = 'CANNOT_BE_NULL',
    CANNOT_BE_EMPTY = 'CANNOT_BE_EMPTY',
    INVALID_VALUE = 'INVALID_VALUE',
    CHOOSE_PROTOCOL = 'CHOOSE_PROTOCOL',
    HTTP_ERROR = 'HTTP_ERROR',
    TIMEOUT = 'TIMEOUT',
    TOO_MANY_REDIRECTS = 'TOO_MANY_REDIRECTS',
    INVALID_DOMAIN_VALUE = 'INVALID_DOMAIN_VALUE'
}

/**
 * Возвращает иконку для типа баннера
 */
export function getIconNameForAdvType(
    type: AdvType | AdvTypeClient,
    campaignType: CampaignType,
    features?: ClientFeatures
): Shape2Icon {
    switch (type) {
        case AdvType.TEXT:
            return Shape2Icon.typeText;
        case AdvTypeClient.TEXT_MOBILE:
            return Shape2Icon.typeText;
        case AdvType.PERFORMANCE:
            return Shape2Icon.typePerformance;
        case AdvType.DYNAMIC:
            return Shape2Icon.typeDynamic;
        case AdvType.MOBILE_CONTENT:
            return Shape2Icon.typeMobileContent;
        case AdvType.MCBANNER:
            return Shape2Icon.typeMCBanner;
        case AdvType.CONTENT_PROMOTION_VIDEO:
            return Shape2Icon.typeContentPromotionVideo;
        case AdvType.CONTENT_PROMOTION_COLLECTION:
            return Shape2Icon.typeContentPromotionCollection;
        case AdvType.CPM_PRICE:
        case AdvType.CPM_BANNER:
            return Shape2Icon.typeCPMBanner;
        case AdvType.CPM_VIDEO:
            return Shape2Icon.typeCPCVideo;
        case AdvType.IMAGE_AD:
            return Shape2Icon.typeImageAd;
        case AdvType.CPC_VIDEO:
            return Shape2Icon.typeCPCVideo;
        case AdvType.CPM_GEOPRODUCT:
            return features && features.isZeroSpeedPageForGeoproductEnabled
                ? Shape2Icon.typeCPMGeoproduct
                : Shape2Icon.typeCPMBannerInMetro;
        case AdvType.CPM_INDOOR:
            return Shape2Icon.typeCPMIndoor;
        case AdvType.CPM_OUTDOOR:
            return Shape2Icon.typeCPMOutdoor;
        case AdvType.CPM_AUDIO:
            return Shape2Icon.typeCPMAudio;
        case AdvType.INTERNAL:
            return getIconNameForCampaignType(campaignType);
        default:
            throw new Error(`Для баннера с типом ${type} не определена иконка`);
    }
}

export const isEditedBannerTypeCpmOutdoor = (banner: EditedBanner): banner is EditedBannerTypeCpmOutdoor => {
    return Boolean(banner) && banner.bannerType === AdvType.CPM_OUTDOOR;
};

/**
 * Возвращает иконку для переключателя превью
 */
export function getIconNameForPreviewType(type: PreviewType): Shape2Icon {
    const dictionary = {
        [PreviewType.NET_GROUP]: Shape2Icon.group,
        [PreviewType.SEARCH_DESC]: Shape2Icon.short,
        [PreviewType.SEARCH_BNO]: Shape2Icon.long,
        [PreviewType.SEARCH_MOBILE]: Shape2Icon.mobile,
        [PreviewType.NET]: Shape2Icon.rsya
    };

    if (!dictionary[type]) {
        throw new Error(`Для баннера с типом ${type} не определена иконка`);
    }

    return dictionary[type];
}

export function getContactsEditorTitleSelectorText(type: ContactsEditorType): string {
    switch (type) {
        case ContactsEditorType.TURBO:
            return i18n('turbo-title');
        case ContactsEditorType.VCARD:
            return i18n('vcard-title');
        case ContactsEditorType.LINK:
            return i18n('link-title');
        case ContactsEditorType.ORGANIZATION:
            return i18n('organization-title');
        default:
            throw `Cannot get text for type ${type}`;
    }
}

/**
 * Возвращает размер иконки для переключателя превью
 */
export function getIconSizeForPreviewType(type: PreviewType): { width: number; height: number } {
    const dictionary = {
        [PreviewType.NET_GROUP]: { width: 16, height: 16 },
        [PreviewType.SEARCH_DESC]: { width: 16, height: 16 },
        [PreviewType.SEARCH_BNO]: { width: 16, height: 16 },
        [PreviewType.SEARCH_MOBILE]: { width: 16, height: 16 },
        [PreviewType.NET]: { width: 16, height: 16 }
    };

    if (!dictionary[type]) {
        throw new Error(`Для баннера с типом ${type} не определен размер иконки`);
    }

    return dictionary[type];
}

export const AdvTypeTitles = {
    [AdvType.TEXT]: i18n('banner-type-text'),
    [AdvTypeClient.TEXT_MOBILE]: i18n('banner-type-text-mobile'),
    [AdvType.PERFORMANCE]: i18n('banner-type-performance'),
    [AdvType.DYNAMIC]: i18n('banner-type-dynamic'),
    [AdvType.MOBILE_CONTENT]: i18n('banner-type-mobile_content'),
    [AdvType.MCBANNER]: i18n('banner-type-mcbanner'),
    [AdvType.CONTENT_PROMOTION_VIDEO]: i18n('banner-type-content-promotion-video'),
    [AdvType.CONTENT_PROMOTION_COLLECTION]: i18n('banner-type-content-promotion-collection'),
    [AdvType.CPM_AUDIO]: i18n('banner-type-cpm_audio'),
    [AdvType.CPM_BANNER]: i18n('banner-type-cpm_banner'),
    [AdvType.CPM_PRICE]: 'CPM PRICE',
    [AdvType.CPM_VIDEO]: i18n('banner-type-cpm_video'),
    [AdvType.IMAGE_AD]: i18n('banner-type-image_ad'),
    [AdvType.CPC_VIDEO]: i18n('banner-type-cpc_video'),
    [AdvType.CPM_OUTDOOR]: i18n('banner-type-cpm_outdoor'),
    [AdvType.CPM_INDOOR]: i18n('banner-type-cpm-indoor'),
    [AdvType.CPM_GEOPRODUCT]: i18n('banner-type-cpm_geoproduct'),
    [AdvType.INTERNAL]: i18n('banner-type-internal')
};

export function getTitleForAdvType(
    type: AdvType | AdvTypeClient,
    campaignType: CampaignType,
    features?: ClientFeatures
): string {
    if (!AdvTypeTitles[type]) {
        throw new Error(`Для значения ${type} не определено название`);
    }

    if (type === AdvType.INTERNAL) {
        switch (campaignType) {
            case CampaignType.INTERNAL_DISTRIB:
                return i18n('banner-type-internal-distrib');
            case CampaignType.INTERNAL_FREE:
                return i18n('banner-type-internal-free');
            case CampaignType.INTERNAL_AUTOBUDGET:
                return i18n('banner-type-internal-autobudget');
            default:
                throw new Error(`Для кампании с типом ${type} не определён тайтл для баннеров внутренней рекламы`);
        }
    }

    if (type === AdvType.CPM_GEOPRODUCT && features && features.isZeroSpeedPageForGeoproductEnabled) {
        return i18n('banner-type-cpm_geoproduct-new');
    }

    return AdvTypeTitles[type];
}

export const AdvTypePlatformNames = {
    [AdvType.CONTENT_PROMOTION_VIDEO]: i18n('yandex-video'),
    [AdvType.CONTENT_PROMOTION_COLLECTION]: i18n('yandex-collections')
};

export function getPlatformNameForAdvType(type: AdvType | AdvTypeClient): string {
    if (!AdvTypePlatformNames[type]) {
        throw new Error(`Для значения ${type} не определено название платформы`);
    }
    return AdvTypePlatformNames[type];
}

export function getEditBannerHrefValidateText(code: HrefValidationStatuses): string {
    const dictionary = {
        [HrefValidationStatuses.CANNOT_BE_EMPTY]: i18n('banner-href-null'),
        [HrefValidationStatuses.CANNOT_BE_NULL]: i18n('banner-href-null'),
        [HrefValidationStatuses.HTTP_ERROR]: i18n('banner-href-http-invalid'),
        [HrefValidationStatuses.INVALID_VALUE]: i18n('banner-href-invalid'),
        [HrefValidationStatuses.CHOOSE_PROTOCOL]: i18n('banner-href-choose-protocol'),
        [HrefValidationStatuses.TIMEOUT]: i18n('banner-href-http-timeout'),
        [HrefValidationStatuses.INVALID_DOMAIN_VALUE]: i18n('banner-href-domain-invalid'),
        [HrefValidationStatuses.TOO_MANY_REDIRECTS]: i18n('banner-href-http-invalid')
    };

    return dictionary[code] || i18n('banner-href-invalid');
}

export function createContentPromotionUrlErrorFromValidationError(
    error: ValidationResultItem | undefined,
    bannerType: AdvType | AdvTypeClient
): ValidationErrorData {
    const errorCode = get(error, 'code', '');
    return {
        text: mapErrorCodeToMessage('banners[0].contentPromotionUrl', errorCode, { bannerType })
    };
}

export function createContentPromotionUrlErrorFromApiError(error: ApiMethodExecutingError): ValidationErrorData {
    const errorCode = get(error.getErrors(), '[0].code', '');
    return {
        text: mapErrorCodeToMessage('banners[0].contentPromotionUrl', errorCode)
    };
}

export function getTitleForBannerStatus(statusType: GdAdPrimaryStatus): string {
    const dictionary = {
        [GdAdPrimaryStatus.ACTIVE]: i18n('active-status'),
        [GdAdPrimaryStatus.ARCHIVED]: i18n('archived-status'),
        [GdAdPrimaryStatus.DRAFT]: i18n('draft-status'),
        [GdAdPrimaryStatus.MANUALLY_SUSPENDED]: i18n('manually-suspended-status'),
        [GdAdPrimaryStatus.MODERATION]: i18n('moderation-status'),
        [GdAdPrimaryStatus.TEMPORARILY_SUSPENDED]: i18n('temporarly-suspended-status'),
        [GdAdPrimaryStatus.MODERATION_REJECTED]: i18n('moderation-rejected-status')
    };

    if (!dictionary[statusType]) {
        throw new Error(`Для значения ${statusType} не определено название`);
    }

    return dictionary[statusType];
}

export function getInactiveResourcesHint(resources: GdAdResources[]): string {
    if (resources.length === 1) {
        const resourceModerationRejectHint: Record<GdAdResources, string> = {
            [GdAdResources.BANNER_IMAGES]: i18n('images-resource-rejected'),
            [GdAdResources.CALLOUTS]: i18n('callouts-resource-rejected'),
            [GdAdResources.CREATIVES]: i18n('creatives-resource-rejected'),
            [GdAdResources.IMAGES]: i18n('images-resource-rejected'),
            [GdAdResources.SITELINKS]: i18n('sitelinks-resource-rejected'),
            [GdAdResources.TURBOLANDINGS]: i18n('turbolandings-resource-rejected'),
            [GdAdResources.VCARD]: i18n('vcard-resource-rejected'),
            [GdAdResources.VIDEO_ADDITIONS]: i18n('video-addtions-resource-rejected'),
            [GdAdResources.DISPLAY_HREF]: i18n('display-href-resource-rejected')
        };

        return resourceModerationRejectHint[resources[0]];
    }

    const resourceName: Record<GdAdResources, string> = {
        [GdAdResources.BANNER_IMAGES]: i18n('images-resource-name'),
        [GdAdResources.CALLOUTS]: i18n('callouts-resource-name'),
        [GdAdResources.CREATIVES]: i18n('creatives-resource-name'),
        [GdAdResources.IMAGES]: i18n('images-resource-name'),
        [GdAdResources.SITELINKS]: i18n('sitelinks-resource-name'),
        [GdAdResources.TURBOLANDINGS]: i18n('turbolandings-resource-name'),
        [GdAdResources.VCARD]: i18n('vcard-resource-name'),
        [GdAdResources.VIDEO_ADDITIONS]: i18n('video-addtions-resource-name'),
        [GdAdResources.DISPLAY_HREF]: i18n('display-href-resource-name')
    };

    return i18n('rejected-on-moderation-resources', {
        resources: resources.map(resource => resourceName[resource]).join(', ')
    });
}

export function getHintForBannerStatus(status: BannerStatus): string | undefined {
    const { isRejectedOnModeration, isPreviousVersionShown, inactiveResources, isPlacementPagesRequired } = status;

    if (isRejectedOnModeration) {
        return i18n('is-rejected-on-moderation');
    }

    if (isPlacementPagesRequired) {
        return i18n('placement-pages-rejected');
    }

    if (inactiveResources && inactiveResources.length !== 0) {
        return getInactiveResourcesHint(inactiveResources);
    }

    if (isPreviousVersionShown) {
        return i18n('is-previous-version-shown');
    }

    return undefined;
}

export const isTextBannerCheck = (bannerType: AdvType): boolean =>
    [AdvType.TEXT, AdvType.MOBILE_CONTENT, AdvType.DYNAMIC].includes(bannerType);

export const isCpcVideoBannerCheck = (bannerType: AdvType): boolean => bannerType === AdvType.CPC_VIDEO;

export const isPcodeBannerCheck = (banner: BannerPreviewData): boolean =>
    banner.group.campaign.strategy.platform === GdCampaignPlatform.CONTEXT && banner.bannerType === AdvType.TEXT;

export const CONTENT_PROMOTION_BANNER_TYPES = [AdvType.CONTENT_PROMOTION_COLLECTION, AdvType.CONTENT_PROMOTION_VIDEO];
export const isContentPromotionBannerCheck = (bannerType: AdvType): boolean =>
    CONTENT_PROMOTION_BANNER_TYPES.includes(bannerType);

export const mapSiteLinkQueryResponseToSiteLinkData = (
    siteLink: adsRowFragmentForEdit_GdTextAd_sitelinks
): SiteLinkData => {
    const href = siteLink.href || '';

    return {
        title: siteLink.title || '',
        descr: siteLink.description || '',
        turbo: siteLink.turbolanding
            ? {
                  name: siteLink.turbolanding.name,
                  href: siteLink.turbolanding.href,
                  landingId: String(siteLink.turbolanding.id)
              }
            : undefined,
        link: UrlHelpers.deleteProtocol(href),
        protocol: (UrlHelpers.extractProtocol(href) as ProtocolValue) || ProtocolValue.https,
        isTurboLoading: false,
        isUrlChecking: false,
        isUrlValid: true
    };
};

export function getSiteLinksFromQueryResult(
    sitelinks?: adsRowFragmentForEdit_GdTextAd_sitelinks[] | null
): Array<SiteLinkData> {
    if (!sitelinks) {
        return [...range(4).map(() => getDefaultSiteLinkData())];
    }

    return sitelinks.length >= 4
        ? [...sitelinks.map(mapSiteLinkQueryResponseToSiteLinkData)]
        : [
              ...sitelinks.map(mapSiteLinkQueryResponseToSiteLinkData),
              ...range(DEFAULT_SITELINKS_COUNT - sitelinks.length).map(() => getDefaultSiteLinkData())
          ];
}

export function getOnVideoPreviewClose(type: string): string {
    switch (type) {
        case 'video':
            return i18n('on-video-creative-close');
        default:
            return i18n('on-video-creative-close');
    }
}

export interface MessengerTypeItem {
    val: string;
    text: string;
}

export function getMessengerTypesItems(): Array<MessengerTypeItem> {
    return [
        {
            text: i18n('default'),
            val: MessengerType.DEFAULT
        },
        // TODO DIRECT-89047 расскоментировать в поддержке новых мессенджеров
        // {
        //     text: i18n('telegram'),
        //     val: MessengerType.TELEGRAM
        // },
        // {
        //     text: i18n('whatsapp'),
        //     val: MessengerType.WHATSAPP
        // },
        // {
        //     text: i18n('viber'),
        //     val: MessengerType.VIBER
        // },
        {
            text: i18n('icq'),
            val: MessengerType.ICQ
        },
        {
            text: i18n('jabber'),
            val: MessengerType.JABBER
        },
        {
            text: i18n('skype'),
            val: MessengerType.SKYPE
        },
        {
            text: i18n('mailru'),
            val: MessengerType.MAIL_AGENT
        }
    ];
}

export function getMessengerTypeText(type: MessengerType): string {
    switch (type) {
        case MessengerType.ICQ:
            return i18n('icq');
        // case MessengerType.TELEGRAM: // TODO DIRECT-89047 сделать поддержку других мессенджеров
        //     return i18n('telegram');
        // case MessengerType.WHATSAPP:
        //     return i18n('whatsapp');
        // case MessengerType.VIBER:
        //     return i18n('viber');
        case MessengerType.SKYPE:
            return i18n('skype');
        case MessengerType.MAIL_AGENT:
            return i18n('mailru');
        case MessengerType.JABBER:
            return i18n('jabber');
        case MessengerType.DEFAULT:
            return i18n('default');
        default:
            return i18n('default');
    }
}

/**
 * Хелпер для выставления дефолтных данных в визитке (когда её нет)
 */
export function getDefaultVCardData(): VcardData {
    return {
        name: '',
        contactPerson: '',
        ogrn: '',
        phone: {
            codeCountry: '',
            codeRegion: '',
            phoneNumber: '',
            extension: ''
        },
        geoId: 0,
        mail: '',
        office: '',
        descr: '',
        suggest: {},
        address: {
            country: undefined,
            city: undefined,
            street: undefined,
            house: undefined
        },
        messengerType: MessengerType.DEFAULT,
        messengerPhone: '',
        pointOnMap: null,
        periods: [{ week: [0, 1, 2, 3, 4], time: { start: '10:00', end: '18:00' } }],
        metroStationId: '0',
        metroStationName: null
    };
}

export function getDefaultEditedBannerPrice(currency: CurrencyCode): EditedBannerPrice {
    return {
        isOpen: false,
        isOnProgress: false,
        bannerPrice: getDefaultBannerPriceData(currency)
    };
}

function getDefaultAdCurrency(currency?: CurrencyCode | GdAdPriceCurrency): GdAdPriceCurrency {
    if (currency && GdAdPriceCurrency[currency]) {
        return GdAdPriceCurrency[currency];
    }
    return GdAdPriceCurrency.RUB;
}

/**
 * Возвращает объект цены объявления с заданной валютой по-умолчанию.
 *
 * В качестве валюты по-умолчанию может быть задана одна из рабочих валют клиента (enum CurrencyCode)
 * или одна из списка валют, доступных для указания цены объявлении (enum GdAdPriceCurrency).
 * Если какая-то рабочая валюта клиента недоступна для установки цены в объявлении, будут установлены рубли.
 *
 * @param currency - валюта, для установки по умолчанию в цене объявления.
 */
export function getDefaultBannerPriceData(currency: CurrencyCode | GdAdPriceCurrency): BannerPrice {
    return {
        prefix: PrefixPrice.DEFAULT,
        price: '',
        priceOld: '',
        currency: getDefaultAdCurrency(currency)
    };
}

export function getBannerPriceData(
    bannerPrice: adsRowFragmentForEdit_GdTextAd_bannerPrice,
    workCurrency: CurrencyCode
): BannerPrice {
    return {
        price: bannerPrice.price || '',
        priceOld: bannerPrice.priceOld || '',
        prefix: bannerPrice.prefix ? PrefixPrice[bannerPrice.prefix] : PrefixPrice.DEFAULT,
        currency: getDefaultAdCurrency(bannerPrice.currency || workCurrency)
    };
}

export function getBannerPriceDataToApi(bannerPrice: BannerPrice): GdAdPriceInput | null {
    if (bannerPrice && bannerPrice.price) {
        return {
            price: bannerPrice.price,
            priceOld: bannerPrice.priceOld,
            prefix: GdAdPricePrefixInput[bannerPrice.prefix] || null,
            currency: GdAdPriceCurrencyInput[bannerPrice.currency]
        };
    }

    return null;
}

export const getAdditions = memoize(
    (additionsFlags: AdditionsFlags): string => {
        const {
            hasWideImage,
            hasTurbolanding,
            hasTurboGallery,
            hasCallouts,
            hasPrice,
            hasVideo,
            hasSitelink
        } = additionsFlags;
        const separator = '. ';
        const additionsList = [];

        if (hasWideImage) {
            additionsList.push(i18n('wide-image'));
        } else if (hasWideImage !== undefined) {
            additionsList.push(i18n('standard-image'));
        }

        if (hasTurbolanding) {
            additionsList.push(i18n('turbolanding'));
        }

        if (hasTurboGallery) {
            additionsList.push(i18n('turbo-gallery'));
        }

        if (hasCallouts) {
            additionsList.push(i18n('callouts'));
        }

        if (hasPrice) {
            additionsList.push(i18n('price'));
        }

        if (hasVideo) {
            additionsList.push(i18n('video-addition'));
        }

        if (hasSitelink) {
            additionsList.push(i18n('sitelink'));
        }

        return additionsList.join(separator);
    },
    (additionsFlags: AdditionsFlags) => {
        const {
            hasWideImage,
            hasTurbolanding,
            hasTurboGallery,
            hasCallouts,
            hasPrice,
            hasVideo,
            hasSitelink
        } = additionsFlags;

        return `${hasWideImage} && ${hasTurbolanding} && ${hasTurboGallery} && ${hasCallouts} && ${hasPrice} && ${hasVideo} && ${hasSitelink}`;
    }
);

export function getTimeFromApi(hours: number, minutes: number, isEndTime?: boolean): string {
    if (isEndTime && hours === 0 && minutes === 0) {
        return '24:00';
    }
    return `${hours >= 10 ? hours : `0${hours}`}:${minutes >= 10 ? minutes : `0${minutes}`}`;
}

export function getCityForMetroStationsFromGeoId(geoId: number): CityWithMetroStations {
    switch (geoId) {
        case 213:
            return CityWithMetroStations.MOSCOW;
        case 2:
            return CityWithMetroStations.SPB;
        case 143:
            return CityWithMetroStations.KYIV;
        default:
            throw Error(`geoId=${geoId} is unsupported`);
    }
}

/**
 * Сейчас только знаем, что метро есть в 3 городах
 * 213 - Москва
 * 2 - Санкт-Петербург
 * 143 - Киев
 * @param geoId
 */
export function isCityWithMetro(geoId: number): boolean {
    return [213, 2, 143].indexOf(geoId) !== -1;
}

export function getMessengerTypeFromApi(messenger: string | null): MessengerType {
    switch (messenger) {
        case 'ICQ':
            return MessengerType.ICQ;
        case 'JABBER':
            return MessengerType.JABBER;
        case 'MAIL_AGENT':
            return MessengerType.MAIL_AGENT;
        case 'SKYPE':
            return MessengerType.SKYPE;
        default:
            return MessengerType.DEFAULT;
    }
}

export function getVCardsIds(data: BannersEditApiData | BannersEditApiDataCopy): Array<string> {
    const vcardsIds: Array<string> = [];

    Object.keys(data.banners).forEach(bannerId => {
        const banner = getEditedBannerWithVcard(data.banners[bannerId]);
        if (banner && banner.vCardId) {
            vcardsIds.push(banner.vCardId);
        }
    });

    return sortedUniq(vcardsIds);
}

export enum BannerTextImageError {
    MIN_SIZE_16_9,
    MIN_SIZE,
    WIDE_SCALE,
    NARROW_SCALE
}

export const validateBannerTextImage = (
    pixelCrop: PixelCrop,
    format: CropFormats
): BannerTextImageError | undefined => {
    const isWide = format === CropFormats.WIDE;
    const { height, width } = pixelCrop;
    const selectedAspectRatio = width / height;

    // если соотношение 16:9, то проверяем минимальный размер 1080x607
    if (isWide && (width < 1080 || height < 607)) {
        return BannerTextImageError.MIN_SIZE_16_9;
    }

    // иначе минимальный размер 450x450 и соотношение от 3:4 до 4:3
    if (!isWide) {
        if (width < 450 || height < 450) {
            return BannerTextImageError.MIN_SIZE;
        }

        if (selectedAspectRatio - 4 / 3 > 0.01 || 3 / 4 - selectedAspectRatio > 0.01) {
            return width > height ? BannerTextImageError.WIDE_SCALE : BannerTextImageError.NARROW_SCALE;
        }
    }

    return;
};

interface GetBannersForSave {
    bannersToSave: Record<BannerId, EditedBanner>;
    smartBannersToSave: Record<BannerId, EditedBannerTypePerformance>;
    cpmBannersToSave: Record<BannerId, EditedCpmBanner>;
    mcBannersToSave: Record<BannerId, EditedBannerTypeMcBanner>;
    contentPromotionBannersToSave: Record<BannerId, EditedContentPromotionBanner>;
}

export const getBannersForSave = (
    banners: Record<BannerId, EditedBanner>,
    video: VideoState,
    creatives: CreativeState,
    turbolandings: Record<EditorPlaceId, EditedBannerTurbolanding>,
    features: OperatorFeaturesFragment,
    accessRights: AccessRights
): GetBannersForSave => {
    const availableCampaignType: Array<AdvType | AdvTypeClient> = getEditableBannerTypes(features, accessRights);

    return (
        Object.keys(banners)
            // отправляем на сервер только те баннеры, которые
            .filter(
                bannerId =>
                    !banners[bannerId].markedForDeletion &&
                    !banners[bannerId].readOnly &&
                    !banners[bannerId].isSaved && // еще не сохранены
                    availableCampaignType.includes(banners[bannerId].bannerType) && // тип поддерживается
                    banners[bannerId].isEditAllow // доступны для редактирования
            )
            .reduce(
                (result, bannerId) => {
                    const bannerVideo = video[bannerId];
                    const turbolanding = turbolandings[bannerId];
                    const creative = creatives[bannerId];
                    const banner: EditedBanner = { ...banners[bannerId] };

                    switch (banner.bannerType) {
                        case AdvType.PERFORMANCE:
                            result.smartBannersToSave[bannerId] = banner;
                            break;

                        case AdvType.MCBANNER:
                            result.mcBannersToSave[bannerId] = banners[bannerId];
                            break;

                        case AdvType.CONTENT_PROMOTION_VIDEO:
                        case AdvType.CONTENT_PROMOTION_COLLECTION:
                            result.contentPromotionBannersToSave[bannerId] = banner;
                            break;

                        case AdvType.CPM_BANNER:
                        case AdvType.CPM_VIDEO:
                        case AdvType.CPM_GEOPRODUCT:
                        case AdvType.CPM_OUTDOOR:
                        case AdvType.CPM_AUDIO:
                        case AdvType.CPM_INDOOR:
                            result.cpmBannersToSave[bannerId] = banner;

                            if (isTurboAvailable(turbolanding, banner)) {
                                result.cpmBannersToSave[bannerId].contacts.turbolanding = turbolanding;
                            }

                            if (bannerVideo) {
                                result.cpmBannersToSave[bannerId].video = bannerVideo;
                            }

                            if (creative) {
                                result.cpmBannersToSave[bannerId].creative = creative;
                            }

                            break;

                        default:
                            result.bannersToSave[bannerId] = banner;

                            if (bannerVideo) {
                                result.bannersToSave[bannerId].video = bannerVideo;
                            }

                            const bannerWithContacts = getEditedBannerWithFullContacts(banner);

                            if (isTurboAvailable(turbolanding, banner)) {
                                result.bannersToSave[bannerId].contacts.turbolanding = turbolanding;
                            }

                            if (
                                bannerWithContacts &&
                                bannerWithContacts.contacts.desktopAndTabletType === ContactsEditorType.LINK
                            ) {
                                result.bannersToSave[bannerId].contacts.href = bannerWithContacts.contacts.href;
                            } else {
                                result.bannersToSave[bannerId].contacts = {
                                    ...result.bannersToSave[bannerId].contacts,
                                    href: '',
                                    displayHref: ''
                                };
                            }

                            const bannerWithVCard = getEditedBannerWithVcard(banner);

                            if (
                                bannerWithVCard &&
                                !banner.campaign.isOnlyTurboAvailable &&
                                bannerWithVCard.contacts.desktopAndTabletType === ContactsEditorType.VCARD
                            ) {
                                result.bannersToSave[bannerId].contacts.vCardId = bannerWithVCard.vCardId;
                            } else {
                                result.bannersToSave[bannerId].contacts.vCardId = undefined;
                            }
                    }

                    return result;
                },
                {
                    bannersToSave: {},
                    smartBannersToSave: {},
                    cpmBannersToSave: {},
                    mcBannersToSave: {},
                    contentPromotionBannersToSave: {}
                }
            )
    );
};

export const isTurboAvailable = (turbolanding: EditedBannerTurbolanding, banner: EditedBanner): boolean | undefined => {
    if (banner.bannerType === AdvType.CPM_GEOPRODUCT) {
        return true;
    }

    const bannerWithContacts = getEditedBannerWithFullContacts(banner);

    return (
        turbolanding &&
        bannerWithContacts &&
        (bannerWithContacts.contacts.desktopAndTabletType === ContactsEditorType.TURBO ||
            bannerWithContacts.contacts.mobileType === ContactsEditorType.TURBO)
    );
};

export const splitBannersOnNewAndEdited = (
    banners: Record<BannerId, EditedBanner>
): {
    editedBanners: Record<BannerId, EditedBanner>;
    newBanners: Record<BannerId, EditedBanner>;
} => {
    return Object.keys(banners).reduce(
        (result, bannerId) => {
            if (banners[bannerId].isNew) {
                result.newBanners[bannerId] = banners[bannerId];
            } else {
                result.editedBanners[bannerId] = banners[bannerId];
            }
            return result;
        },
        { editedBanners: {}, newBanners: {} }
    );
};

export const getBannersToDelete = <T extends EditedBannerBase>(banners: Record<BannerId, T>): Record<BannerId, T> => {
    return Object.keys(banners).reduce<Record<BannerId, T>>((result, bannerId) => {
        const { isEditAllow, markedForDeletion } = banners[bannerId];

        if (markedForDeletion && isEditAllow) {
            result[bannerId] = banners[bannerId];
        }
        return result;
    }, {});
};

interface SplitSmartBannersByTypes {
    editedSmartBanners: Record<BannerId, EditedBannerTypePerformance>;
    newSmartBanners: Record<BannerId, EditedBannerTypePerformance>;
}

export const splitSmartBannersByTypes = (
    banners: Record<BannerId, EditedBannerTypePerformance>
): SplitSmartBannersByTypes => {
    return Object.keys(banners).reduce(
        (result, bannerId) => {
            const banner = banners[bannerId];

            if (banner.isNew) {
                result.newSmartBanners[bannerId] = banner;
            } else if (banner.creativeReplaced) {
                result.editedSmartBanners[bannerId] = banner;
            }
            return result;
        },
        { editedSmartBanners: {}, newSmartBanners: {} }
    );
};

interface SplitMcBannersByTypes {
    editedMcBanners: Record<BannerId, EditedBannerTypeMcBanner>;
    newMcBanners: Record<BannerId, EditedBannerTypeMcBanner>;
}

export const splitMcBannersByTypes = (banners: Record<BannerId, EditedBannerTypeMcBanner>): SplitMcBannersByTypes => {
    return Object.keys(banners).reduce(
        (result, bannerId) => {
            const banner = banners[bannerId];

            if (banner.isNew) {
                result.newMcBanners[bannerId] = banner;
            } else {
                result.editedMcBanners[bannerId] = banner;
            }
            return result;
        },
        { editedMcBanners: {}, newMcBanners: {} }
    );
};

interface SplitCpmBannersByTypes {
    editedCpmBanners: Record<BannerId, EditedCpmBanner>;
    newCpmBanners: Record<BannerId, EditedCpmBanner>;
}

export const splitCpmBannersByTypes = (banners: Record<BannerId, EditedCpmBanner>): SplitCpmBannersByTypes => {
    return Object.keys(banners).reduce(
        (result, bannerId) => {
            const banner = banners[bannerId];

            if (banner.isNew) {
                result.newCpmBanners[bannerId] = banner;
            } else {
                result.editedCpmBanners[bannerId] = banner;
            }
            return result;
        },
        { editedCpmBanners: {}, newCpmBanners: {} }
    );
};

interface SplitContentPromotionBannersByTypes {
    editedContentPromotionBanners: Record<BannerId, EditedContentPromotionBanner>;
    newContentPromotionBanners: Record<BannerId, EditedContentPromotionBanner>;
}

export const splitContentPromotionBannersByTypes = (
    banners: Record<BannerId, EditedContentPromotionBanner>
): SplitContentPromotionBannersByTypes => {
    return Object.keys(banners).reduce(
        (result, bannerId) => {
            const banner = banners[bannerId];

            if (banner.isNew) {
                result.newContentPromotionBanners[bannerId] = banner;
            } else {
                result.editedContentPromotionBanners[bannerId] = banner;
            }
            return result;
        },
        { editedContentPromotionBanners: {}, newContentPromotionBanners: {} }
    );
};

/**
 * Типограф для бедных
 * @param {String} text Исходный текст
 * @returns {String} "Облагороженный" текст
 */
export const prettifyText = (text: string): string => {
    // не обрабатываем укр. объявления
    if (/[\u0404\u0406\u0407\u0454\u0456\u0457\u0490\u0491]/.test(text)) {
        return text;
    }

    const RUS_LETTERS = '\u0430-\u043F\u0440-\u044F\u0410-\u042F\u0451\u0401';
    const RUS_LAT_LETTERS = 'a-zA-Z' + RUS_LETTERS;

    const dashRegexp = new RegExp('([' + RUS_LAT_LETTERS + ']) - ([' + RUS_LAT_LETTERS + '])', 'g'),
        // [Аа]|[Бб]ез|[Вв](?:|ы|ас|ам|се|сё)|[Гг]де|[Дд](?:о|ля)|[Зз]а|[Ии](?:|з)|[Кк](?:|о|ак)|[Мм]ы|[Нн](?:а|ам|ас|е|и|о)|[Оо](?:|б|т)|[Пп](?:о|ро)|[Сс](?:|о)|[Тт](?:о|ут|ы)|[Уу]|[Чч]то|[Ээ]то
        rusParts =
            '[\u0410\u0430]|[\u0411\u0431]\u0435\u0437|[\u0412\u0432](?:|\u044B|\u0430\u0441|\u0430\u043C|\u0441\u0435|\u0441\u0451)|[\u0413\u0433]\u0434\u0435|[\u0414\u0434](?:\u043E|\u043B\u044F)|[\u0417\u0437]\u0430|[\u0418\u0438](?:|\u0437)|[\u041A\u043A](?:|\u043E|\u0430\u043A)|[\u041C\u043C]\u044B|[\u041D\u043D](?:\u0430|\u0430\u043C|\u0430\u0441|\u0435|\u0438|\u043E)|[\u041E\u043E](?:|\u0431|\u0442)|[\u041F\u043F](?:\u043E|\u0440\u043E)|[\u0421\u0441](?:|\u043E)|[\u0422\u0442](?:\u043E|\u0443\u0442|\u044B)|[\u0423\u0443]|[\u0427\u0447]\u0442\u043E|[\u042D\u044D]\u0442\u043E',
        nbsp = '\u00A0',
        dash = '\u2013'; // &ndash;, если надо &mdash;, то будет \u2014

    return (
        text
            // больше одного пробела подряд
            .replace(/([\s\u00A0]){2,}/g, '$1')
            // убираем висячие короткие союзы/частицы (склеиваем через nbsp)
            .replace(
                new RegExp('(^|\\(|\\s)(' + rusParts + ')\\s([\u00AB"$\\d' + RUS_LAT_LETTERS + '])', 'g'),
                '$1$2' + nbsp + '$3'
            )
            // убираем пробелы перед некоторыми знаками препинания
            .replace(/[\s\u00A0]+([.,!?])/g, '$1')
            // заменяем на ndash, потому что mdash обычно слишком широкий
            .replace(dashRegexp, '$1' + nbsp + dash + ' $2')
            // заменяем в информации о скидках вида:
            //    Скидка -50%
            // дефис на нормальный минус (критично в шрифтах типа Arial)
            .replace(/ \-(\d\d?)%/g, ' \u2212$1%')
            // висячие слова в конце объявления длиной 1-2 символа склеиваем с основной строкой
            .replace(/ ([^\s]{1,2})$/, nbsp + '$1')
            // неразрываемый пробел в годах, вида 2012 г.
            .replace(/(19\d\d|20[012]\d)[\s\u00A0]*(\u0433\.?)/g, '$1' + nbsp + '$2')
            // неразрываемый пробел в ценах в рублях, вида 5 000 руб
            .replace(/(\d+ (\d{3} ){0,2})\u0440\u0443\u0431([.,?!:;\s]|$)/g, function(
                str: string,
                p1: string,
                p2: string,
                p3: string
            ): string {
                return p1.replace(/ /g, nbsp) + '\u0440\u0443\u0431' + p3;
            })
            .replace(/\n+/g, ' ')
    );
};

/**
 * Форматирует значения для отображения в "видимой ссылке" баннера
 *
 * @param {String} value
 * @returns {string}
 */
export const prettifyDisplayHref = (value: string): string => {
    // şŞıİçÇöÖüÜĞğ - турецкие символы
    // ІіЇїЄєҐґ - украинские символы
    // ӘҒҚҢӨҮҰҺәғқңөүұһұү - казахские символы
    // ҐґІіЎў - белорусские символы

    return (value || '')
        .replace(/[ _]/g, '-')
        .replace(/[^a-zA-ZА-Яа-яЁё0-9şŞıİçÇöÖüÜĞğІіЇїЄєҐґӘҒҚҢӨҮҰҺәғқңөүұһұүЎў\/№#%-]/g, '')
        .replace(/[\/]+/g, '/')
        .replace(/[-]+/g, '-');
};

export const mapAdImageFormatsFragmentToBannerImageFormat = (
    formats: Array<adImageFormatsFragment>
): Array<BannerImageFormat> => {
    return formats.map((format: adImageFormatsFragment) => {
        return {
            url: format.path,
            width: format.imageSize.width,
            height: format.imageSize.height,
            smartCenters: format.smartCenters
                ? format.smartCenters.map(
                      (smartCenter: adImageSmartCentersFragment): BannerImageSmartCenter => {
                          return {
                              x: smartCenter.x,
                              y: smartCenter.y,
                              w: smartCenter.width,
                              h: smartCenter.height,
                              ratio: smartCenter.ratio
                          };
                      }
                  )
                : null
        } as BannerImageFormat;
    });
};

export const mapUploadImageResultFormatToBannerImageFormat = (
    formats: Array<UploadImageResultFormatsApi>
): Array<BannerImageFormat> | undefined => {
    // TODO надо будет выпилить проверку на опциональное поле, когда починят https://st.yandex-team.ru/DIRECT-103229

    return formats
        ? formats.map((format: UploadImageResultFormatsApi) => {
              return {
                  url: format.path,
                  width: format.image_size.width,
                  height: format.image_size.height,
                  smartCenters: format.smart_centers
                      ? format.smart_centers.map(
                            (smartCenter: UploadImageResultSmartCenter): BannerImageSmartCenter => {
                                return {
                                    x: smartCenter.x,
                                    y: smartCenter.y,
                                    w: smartCenter.width,
                                    h: smartCenter.height,
                                    ratio: smartCenter.ratio
                                };
                            }
                        )
                      : null
              } as BannerImageFormat;
          })
        : undefined;
};

/**
 * Подготавлиает данные о форматах изображения, пришедшие с бэка, для нужно вида в PCode
 * Исполользуется в качестве смарт-центров при орисовке баннеров разного размера
 *
 * @param {Array<BannerImageFormat>} formats
 */
export const getImageFormatsFromApiForPcodeRenderDataImages = (
    formats: Array<BannerImageFormat>
): Array<PcodeAdsImage> => {
    return formats.map((format: BannerImageFormat) => {
        return [
            format.url.startsWith('http') ? format.url : UrlHelpers.getAbsoluteImageUrlForPcode(format.url),
            format.width,
            format.height,
            {
                'smart-centers': format.smartCenters
            }
        ] as PcodeAdsImage;
    });
};

export const convertSitelinkErrorIndex = (
    sitelinkErrorIndex: number,
    sitelinksList: Array<GdAddSitelinkInput | null>
): number => {
    let sitelinkIndex = 0;
    let filledIndex = 0;

    sitelinksList.find((sitelink: GdAddSitelinkInput, index: number) => {
        if (sitelink !== null) {
            if (sitelinkErrorIndex === filledIndex) {
                sitelinkIndex = index;
                return true;
            }
            filledIndex++;
        }
        return false;
    });

    return sitelinkIndex;
};

export const isBannerPriceEditable = (banner: Banner): boolean => {
    const { group } = banner;
    const editableTypes = [AdvType.TEXT, AdvTypeClient.TEXT_MOBILE];
    const campaignStatusArchived = group.campaign.statusInfo.type === GdCampaignPrimaryStatus.ARCHIVED;
    const groupStatusArchived = group.status.primaryStatus === GdAdGroupPrimaryStatus.ARCHIVED;
    const bannerStatusArchived = banner.status.type === GdAdPrimaryStatus.ARCHIVED;
    const isArchived = campaignStatusArchived || groupStatusArchived || bannerStatusArchived;

    return editableTypes.includes(banner.bannerType) && banner.access.canEdit && !isArchived;
};

export const isBannerOrganizationEditable = (banner: Banner): boolean => {
    const { group } = banner;
    const editableTypes = [AdvType.TEXT, AdvTypeClient.TEXT_MOBILE];
    const campaignStatusArchived = group.campaign.statusInfo.type === GdCampaignPrimaryStatus.ARCHIVED;
    const groupStatusArchived = group.status.primaryStatus === GdAdGroupPrimaryStatus.ARCHIVED;
    const bannerStatusArchived = banner.status.type === GdAdPrimaryStatus.ARCHIVED;
    const isArchived = campaignStatusArchived || groupStatusArchived || bannerStatusArchived;

    return editableTypes.includes(banner.bannerType) && banner.access.canEdit && !isArchived;
};

export const defineSmartBannerImageSizeText = (width: number, height: number): string => {
    const isAdaptive = height === 0 || width === 0;

    return isAdaptive ? i18n('banner-adaptive-image') : `${width}x${height}`;
};

export const mapWorkingPeriodsToSerpPreviewData = (periods: Array<WorkPeriodInfo>): string => {
    return periods
        .map(period => {
            if (period.week.length === 1) {
                return `${moment.weekdaysShort(convertDayToMomentApiDay(period.week[0]))}: ${period.time.start}-${
                    period.time.end
                }`;
            } else {
                return `${moment.weekdaysShort(convertDayToMomentApiDay(period.week[0]))}-${moment.weekdaysShort(
                    convertDayToMomentApiDay(period.week[period.week.length - 1])
                )}: ${period.time.start}-${period.time.end}`;
            }
        })
        .join(', ');
};

export const convertDayToMomentApiDay = (day: Day): number => {
    return day === 6 ? 0 : day + 1;
};

export const mapPhoneToSerpPreviewData = (phone: PhoneNumberData): string => {
    if (phone.codeCountry === '' && phone.codeRegion === '' && phone.phoneNumber === '') {
        return '';
    }

    return phone.extension !== ''
        ? `${phone.codeCountry} (${phone.codeRegion}) ${phone.phoneNumber} (${phone.extension})`
        : `${phone.codeCountry} (${phone.codeRegion}) ${phone.phoneNumber}`;
};

export const hasTemplate = (str: string): boolean => /#([^#]*)#/.test(str);

export const replaceTemplate = (str: string): string => {
    return str
        .split(/(#[^#]*#)/)
        .map(chunk => {
            const matches = chunk.match(/#([^#]*)#/);

            return matches ? matches[1] : chunk;
        })
        .join('');
};

export const getBannersEditMassActions = (mapper: Record<AdvType, number>): BannersEditMassActionItems => {
    return getVisibleBannersEditMassActions(getBannersEditMassActionsItems(mapper));
};

export const getVisibleBannersEditMassActions = (
    groupedItems: BannersEditMassActionItems
): BannersEditMassActionItems => {
    return groupedItems.reduce(
        (acc: BannersEditMassActionItems, next: BannersEditMassActionGroup) => {
            const availableItems = next.items.filter((val: BannersEditMassActionItem) => !val.disabled && !val.hidden);
            const visibleItems = next.items.filter((val: BannersEditMassActionItem) => !val.hidden);

            if (availableItems.length !== 0) {
                acc.push({
                    title: next.title,
                    items: visibleItems
                });
            }

            return acc;
        },
        [] as BannersEditMassActionItems
    );
};

/**
 * Возвращает название по-умолчанию для объявлений
 *
 * Бэк не присылает title объявлениям продвижения Яндекс.Коллекций
 * todo DIRECT-108959 - выпилить, когда бэк начнет присылать названия (тикет на бэк DIRECT-108760)
 */
export const getBannerTitleFallback = (bannerType: AdvType): string => {
    switch (bannerType) {
        case AdvType.CONTENT_PROMOTION_COLLECTION:
            return i18n('yandex-collections');
        default:
            throw new Error(`There is no default title for banner type: "${bannerType}"`);
    }
};

export const getBannersEditMassActionsItems = (advTypeCount: Record<AdvType, number>): BannersEditMassActionItems => {
    // Белый список
    const rulesShow = {
        [BannersEditMassActionItemType.visibleLinkReplace]: [AdvType.TEXT],
        [BannersEditMassActionItemType.siteLinks]: [AdvType.TEXT],
        [BannersEditMassActionItemType.textPathReplace]: [AdvType.TEXT],
        [BannersEditMassActionItemType.callouts]: [AdvType.TEXT],
        [BannersEditMassActionItemType.video]: [AdvType.TEXT],
        [BannersEditMassActionItemType.bannerPrice]: [AdvType.TEXT],
        [BannersEditMassActionItemType.image]: [AdvType.TEXT],
        [BannersEditMassActionItemType.visibleLinkReplace]: [AdvType.TEXT],
        [BannersEditMassActionItemType.videoCreative]: [AdvType.CPC_VIDEO],
        [BannersEditMassActionItemType.pixel]: [AdvType.CPM_BANNER, AdvType.CPM_VIDEO],
        [BannersEditMassActionItemType.counters]: [AdvType.CPM_BANNER, AdvType.CPM_AUDIO, AdvType.CPM_VIDEO],
        [BannersEditMassActionItemType.cpmVideoCreative]: [AdvType.CPM_VIDEO],
        [BannersEditMassActionItemType.audioCreative]: [AdvType.CPM_AUDIO],
        [BannersEditMassActionItemType.imageCreative]: [AdvType.IMAGE_AD, AdvType.MCBANNER],
        [BannersEditMassActionItemType.cpmImageCreative]: [AdvType.CPM_BANNER]
    };

    // Черный список
    const rulesHide = {
        [BannersEditMassActionItemType.turbo]: [
            AdvType.CPM_AUDIO,
            AdvType.MCBANNER,
            AdvType.CONTENT_PROMOTION_VIDEO,
            AdvType.CONTENT_PROMOTION_COLLECTION
        ],
        [BannersEditMassActionItemType.tracking]: [
            AdvType.CPM_AUDIO,
            AdvType.CONTENT_PROMOTION_VIDEO,
            AdvType.CONTENT_PROMOTION_COLLECTION
        ]
    };

    const setHiddenStatus = (item: BannersEditMassActionItem): BannersEditMassActionItem => {
        const showValue = rulesShow[item.val];
        const hideValue = rulesHide[item.val];

        if (showValue) {
            // Нет ниодного типа, входящего в белый список
            item.hidden = !showValue.some((type: AdvType) => Boolean(advTypeCount[type]));
        }

        if (hideValue) {
            item.hidden =
                // Есть ли ходя бы один тип, входящий в черный список
                hideValue.some((type: AdvType) => Boolean(advTypeCount[type])) &&
                // Нет ли других типов, которые не входят в черный список
                !Object.keys(advTypeCount).some(type => !hideValue.includes(type));
        }

        return item;
    };
    const textActionsItemsAvailable: BannersEditMassActionItem[] = [
        {
            type: BannersEditMassActionItemType.textPathReplace,
            text: i18n('text-path-replace'),
            val: BannersEditMassActionItemType.textPathReplace,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.callouts,
            text: i18n('callouts'),
            val: BannersEditMassActionItemType.callouts,
            hidden: false
        }
    ];
    const groupActionsItemsAvailable: BannersEditMassActionItem[] = [
        {
            type: BannersEditMassActionItemType.link,
            text: i18n('link'),
            val: BannersEditMassActionItemType.link,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.linkReplace,
            text: i18n('link-replace'),
            val: BannersEditMassActionItemType.linkReplace,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.visibleDomain,
            text: i18n('visible-domain'),
            val: BannersEditMassActionItemType.visibleDomain,
            hidden: true
        },
        {
            type: BannersEditMassActionItemType.visibleLinkReplace,
            text: i18n('visible-link-replace'),
            val: BannersEditMassActionItemType.visibleLinkReplace,
            hidden: true
        },
        {
            type: BannersEditMassActionItemType.tracking,
            text: i18n('tracking'),
            val: BannersEditMassActionItemType.tracking,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.siteLinks,
            text: i18n('site-links'),
            val: BannersEditMassActionItemType.siteLinks,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.pixel,
            text: i18n('pixel'),
            val: BannersEditMassActionItemType.pixel,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.counters,
            text: i18n('counters'),
            val: BannersEditMassActionItemType.counters,
            hidden: false
        }
    ];
    const notGroupActionsItemsAvailable: BannersEditMassActionItem[] = [
        {
            type: BannersEditMassActionItemType.bannerPrice,
            text: i18n('banner-price'),
            val: BannersEditMassActionItemType.bannerPrice,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.turbo,
            text: i18n('turbo'),
            val: BannersEditMassActionItemType.turbo,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.videoCreative,
            text: i18n('video-creatives'),
            val: BannersEditMassActionItemType.videoCreative,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.cpmVideoCreative,
            text: i18n('video-creatives'),
            val: BannersEditMassActionItemType.cpmVideoCreative,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.audioCreative,
            text: i18n('audio-creatives'),
            val: BannersEditMassActionItemType.audioCreative,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.cpmImageCreative,
            text: i18n('cpm-image-creative'),
            val: BannersEditMassActionItemType.cpmImageCreative,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.imageCreative,
            text: i18n('image-creative'),
            val: BannersEditMassActionItemType.imageCreative,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.image,
            text: i18n('images'),
            val: BannersEditMassActionItemType.image,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.video,
            text: i18n('video'),
            val: BannersEditMassActionItemType.video,
            hidden: false
        },
        {
            type: BannersEditMassActionItemType.ageLimit,
            text: i18n('age-limit'),
            val: BannersEditMassActionItemType.ageLimit,
            hidden: true
        },
        {
            type: BannersEditMassActionItemType.childAgeLimit,
            text: i18n('child-age-limit'),
            val: BannersEditMassActionItemType.childAgeLimit,
            hidden: true
        }
    ];

    const groupMassAction = {
        title: i18n('link-groups-mass-action'),
        items: groupActionsItemsAvailable.map(setHiddenStatus)
    };

    const textMassAction = {
        title: i18n('text-groups-mass-action'),
        items: textActionsItemsAvailable.map(setHiddenStatus)
    };

    const notGroupMassAction = {
        title: i18n('not-grouped-mass-action'),
        items: notGroupActionsItemsAvailable.map(setHiddenStatus)
    };

    return [groupMassAction, textMassAction, notGroupMassAction];
};

export function getTextBannersIds(banners: Record<BannerId, EditedBanner>): BannerId[] {
    return Object.keys(banners)
        .filter(
            key => banners[key].bannerType === AdvType.TEXT || banners[key].bannerType === AdvTypeClient.TEXT_MOBILE
        )
        .map(key => banners[key].id);
}

export function getGroupedBannersIdsByOpenCard(
    banners: Record<BannerId, EditedBanner>,
    draftBanners: Record<BannerId, DraftBanner>,
    siteLinksData: Record<BannerId, BannerSiteLinksEditorData>,
    bannersPriceEditor: Record<EditorBannerPriceId, EditorBannerPriceData>
): BannersIdsGroupsByOpenState {
    return Object.keys(banners).reduce(
        (acc: BannersIdsGroupsByOpenState, next: string) => {
            const banner = banners[next] as EditedBanner;

            if (banner.bannerType !== AdvType.TEXT && banner.bannerType !== AdvTypeClient.TEXT_MOBILE) {
                return acc;
            }

            const { isOpen: isBannerPriceEditorOpen } = banner.bannerPriceEditor;
            const { isOpen: isSiteLinksOpen } = banner.sitelinks;
            const { isEditorOpen } = banner.callouts;

            if (isBannerPriceEditorOpen) {
                const { price, priceOld } = bannersPriceEditor[banner.id].bannerPrice;

                if (price || priceOld) {
                    acc.bannerPricesBannersIds.push(banner.id);
                } else {
                    acc.bannerPricesEmptyIds.push(banner.id);
                }
            }

            if (isSiteLinksOpen) {
                const newSiteLinks = mapBannersSiteLinksToApiFormat([banner.id], siteLinksData);

                const isSiteLinksEmpty = newSiteLinks.every(item => item.sitelinks.length === 0);

                if (isSiteLinksEmpty) {
                    acc.siteLinksEmptyBannersIds.push(banner.id);
                } else {
                    acc.siteLinksBannersIds.push(banner.id);
                }
            }

            if (isEditorOpen) {
                acc.calloutsEditorIds.push(banner.id);
            }

            return acc;
        },
        {
            siteLinksBannersIds: [],
            bannerPricesBannersIds: [],
            calloutsEditorIds: [],
            bannerPricesEmptyIds: [],
            siteLinksEmptyBannersIds: []
        }
    );
}

export const processBanners = <U extends { bannerId: BannerId[] }>(
    bannersMapper?: (
        banner: EditedBanner,
        draftBanner: DraftBanner,
        action: U,
        index: number,
        fullState: State
    ) => EditedBanner,
    bannersDraftMapper?: (
        banner: EditedBanner,
        draftBanner: DraftBanner,
        action: U,
        index: number,
        fullState: State
    ) => DraftBanner
): ((state: EditBannersState, action: U, fullState: State) => EditBannersState) => (
    state: EditBannersState,
    action: U,
    fullState: State
): EditBannersState => {
    const banners = action.bannerId.map(id => state.banners[id]);
    const bannersDraft: DraftBanner[] = action.bannerId.map(id => state.bannersDraft[id]);

    const isBannersEmpty = !banners.some(Boolean);

    if (isBannersEmpty) {
        return state;
    }

    let newBanners = state.banners;

    if (bannersMapper) {
        newBanners = banners.reduce(
            (acc, next, index) => {
                if (!next) {
                    return acc;
                }

                acc[next.id] = bannersMapper(next, bannersDraft[index], action, index, fullState);

                return acc;
            },
            {} as Record<BannerId, EditedBanner>
        );
    }

    let newBannersDraft = state.bannersDraft;

    if (bannersDraftMapper) {
        newBannersDraft = banners.reduce(
            (acc, next, index) => {
                if (!next) {
                    return acc;
                }

                const draftBanners = state.bannersDraft[next.id];

                acc[next.id] = bannersDraftMapper(next, draftBanners, action, index, fullState);

                return acc;
            },
            {} as Record<BannerId, DraftBanner>
        );
    }

    return {
        ...state,
        banners: {
            ...state.banners,
            ...newBanners
        },
        bannersDraft: {
            ...state.bannersDraft,
            ...newBannersDraft
        }
    };
};

export const getSiteLinksCommonTitleLength = (siteLinks: Array<SiteLinkData>): number => {
    return siteLinks.reduce((result: number, next: SiteLinkData) => {
        result += (next.title || '').length;
        return result;
    }, 0);
};

export const getDefaultSiteLinkData = (): SiteLinkData => {
    return {
        title: '',
        link: '',
        descr: '',
        isTurboLoading: false,
        isUrlChecking: false,
        isUrlValid: true,
        protocol: ProtocolValue.https,
        turbo: undefined
    };
};

export function getEmptyBannerImage(): EditedBannerImage {
    return {
        mode: ImageOrCreativeUploadingMode.EMPTY,
        displayName: '',
        imageUrl: '',
        uploadProgress: undefined,
        data: undefined
    };
}

export const checkIsSiteLinkFilled = (siteLink: SiteLinkData): Boolean => {
    return (
        siteLink.title.length !== 0 ||
        siteLink.descr.length !== 0 ||
        (siteLink.link || '').length !== 0 ||
        Boolean(siteLink.turbo)
    );
};

export const checkIsSiteLinksEditorHasLinksErrors = (
    siteLinks: Array<SiteLinkData>,
    siteLinksErrors: { errors?: ValidationErrorData[] } | undefined
): boolean => {
    return siteLinks.some((siteLinkData, index) => {
        const siteLinkError = get(siteLinksErrors, `[${index}].link.errors`, []);

        return siteLinkData.isUrlChecking || siteLinkError.length !== 0;
    });
};

// Функция схлопывает сайтлинки
// Например, заполнили 1, 2, 4 сайтлинк => получили 1, 2, 3 + пустой сайтлинк.
// Минимум на выходе должно быть 4 сайтлинка, при схлопывании добиваем дефолтными значениями
// Если сайтлинков будет больше 4, вернем их
export const getCollapsedSiteLinks = (siteLinks: Array<SiteLinkData>): Array<SiteLinkData> => {
    const filledSiteLinks = siteLinks.filter(checkIsSiteLinkFilled);

    if (filledSiteLinks.length < DEFAULT_SITELINKS_COUNT) {
        return range(DEFAULT_SITELINKS_COUNT - filledSiteLinks.length).reduce((result: Array<SiteLinkData>, index) => {
            return [...result, getDefaultSiteLinkData()];
        }, filledSiteLinks);
    }

    return filledSiteLinks;
};

export const getPureCalloutText = (text: string): string => {
    return text.trim().replace(/ {1,}/g, ' ');
};

export const generateNewBannerId = (campaignId: CampaignId, groupId: GroupId): GroupId => {
    return `new#${campaignId}#${groupId}#${uniqueId()}`;
};

interface PreviewFields {
    title?: string;
    body?: string;
    domain?: string;
}

export function fillBannerEmptyFieldsForPreview<T extends PreviewFields>(banner: T): T {
    return {
        ...banner,
        title: banner.title || i18n('banner-title'),
        body: banner.body || i18n('banner-body'),
        domain: banner.domain || i18n('banner-domain')
    };
}

export function fillBannerEmptyFieldsForContentPromotionPreview(
    banner: EditedContentPromotionBanner
): PreviewContentPromotionBannerProps {
    const bannerTypeToTitlePlaceholder: { [key in ContentPromotionBannerType]?: string } = {
        [AdvType.CONTENT_PROMOTION_VIDEO]: i18n('video-title-placeholder')
    };

    const bannerTypeToBodyPlaceholder: { [key in ContentPromotionBannerType]?: string } = {
        [AdvType.CONTENT_PROMOTION_VIDEO]: i18n('video-body-placeholder')
    };

    return {
        previewUrl: banner.previewUrl || '',
        title: banner.title,
        titlePlaceholder: bannerTypeToTitlePlaceholder[banner.bannerType],
        body: banner.body,
        bodyPlaceholder: bannerTypeToBodyPlaceholder[banner.bannerType],
        url: banner.contacts.href
            ? banner.contacts.hrefProtocol.concat(banner.contacts.href)
            : banner.contentPromotionUrl.value,
        platformName: getPlatformNameForAdvType(banner.bannerType),
        bannerHasTemplate: false
    };
}

const imageCreativeEditorActionsForCanvas = [
    {
        title: i18n('creatives-constructor'),
        items: [
            {
                text: i18n('preset-create-new'),
                val: ImageCreativeEditorActionTypes.presets
            },
            {
                text: i18n('preset-adaptive'),
                val: ImageCreativeEditorActionTypes.adaptive
            },
            {
                text: i18n('creatives-ideas'),
                val: ImageCreativeEditorActionTypes.ideas
            },
            {
                text: i18n('choose-creatives-presets'),
                val: ImageCreativeEditorActionTypes.creatives
            }
        ]
    }
];

const imageCreativeEditorActionsForCreatedCreatives = [
    {
        title: i18n('created-creatives'),
        items: [
            {
                text: i18n('upload-local'),
                val: ImageCreativeEditorActionTypes.local
            },
            {
                text: i18n('upload-link'),
                val: ImageCreativeEditorActionTypes.link
            },
            {
                text: i18n('choose-gallery'),
                val: ImageCreativeEditorActionTypes.gallery
            }
        ]
    }
];

const imageCreativeEditorActionsForNewImageAd = [
    ...imageCreativeEditorActionsForCanvas,
    ...imageCreativeEditorActionsForCreatedCreatives
];

export const getImageCreativeEditorActions = (
    bannerType: AdvType | AdvTypeClient,
    creativeType?: BannerImageType,
    isNew?: boolean
): Array<ActionsMenuGroup> => {
    switch (bannerType) {
        case AdvType.IMAGE_AD:
            if (isNew) {
                return imageCreativeEditorActionsForNewImageAd;
            }
            if (creativeType === BannerImageType.BANNER) {
                return imageCreativeEditorActionsForCreatedCreatives;
            }
            if (creativeType === BannerImageType.CREATIVE) {
                return imageCreativeEditorActionsForCanvas;
            }
            return imageCreativeEditorActionsForNewImageAd;
        case AdvType.MCBANNER:
            return imageCreativeEditorActionsForCreatedCreatives;
        default:
            throw new Error('unsupported AdvType for getImageCreativeEditorActions: ${bannerType}');
    }
};

export const getBannerEditPageItemEditorId = (bannerId: string): string => {
    return `banner-${bannerId}`;
};

export const getGroupedBannersIdsForVisitingCard = (
    state: State,
    bannersIds: string[]
): BannersIdsGroupsByOpenStateForVisitingCard => {
    const resultGroupsBannerIds: BannersIdsGroupsByOpenStateForVisitingCard = {
        bannerIdsIsNeedForUpdatingVisitingCard: [],
        bannerIdsIsEmptyForUpdatingVisitingCard: []
    };

    bannersIds.forEach(bannerId => {
        const visitingCardEditorId = getBannerEditPageItemEditorId(bannerId);
        const currentVisitingCardEditor = selectVisitingCardEditorValue(state, visitingCardEditorId);
        const isEqualEmptyVisitingCard = isEqual(getDefaultVCardData(), currentVisitingCardEditor.vCardData);

        if (currentVisitingCardEditor.isOpen && !isEqualEmptyVisitingCard) {
            resultGroupsBannerIds.bannerIdsIsNeedForUpdatingVisitingCard.push(bannerId);
        } else if (currentVisitingCardEditor.isOpen) {
            resultGroupsBannerIds.bannerIdsIsEmptyForUpdatingVisitingCard.push(bannerId);
        }
    });

    return resultGroupsBannerIds;
};
export const measurerDisplayName: Record<GdBannerMeasurerSystem, string> = {
    [GdBannerMeasurerSystem.ADLOOX]: 'Adloox',
    [GdBannerMeasurerSystem.ADMETRICA]: 'AdMetrica',
    [GdBannerMeasurerSystem.ADRIVER]: 'AdRiver',
    [GdBannerMeasurerSystem.DV]: 'DV',
    [GdBannerMeasurerSystem.INTEGRAL_AD_SCIENCE]: 'Integral Ad Science',
    [GdBannerMeasurerSystem.MEDIASCOPE]: 'Mediascope',
    [GdBannerMeasurerSystem.MOAT]: 'Moat',
    [GdBannerMeasurerSystem.SIZMEK]: 'Sizmek',
    [GdBannerMeasurerSystem.WEBORAMA]: 'Weborama'
};

export const getMeasurers = (
    bannerType: AdvType | AdvTypeClient,
    features: ClientFeatures
): GdBannerMeasurerSystem[] => {
    switch (bannerType) {
        case AdvType.CPM_BANNER:
            return compact([
                features.isAdlooxMeasurerCpmBannerAllowed && GdBannerMeasurerSystem.ADLOOX,
                features.isAdmetricaMeasurerCpmBannerAllowed && GdBannerMeasurerSystem.ADMETRICA,
                features.isAdriverMeasurerCpmBannerAllowed && GdBannerMeasurerSystem.ADRIVER,
                features.isDvMeasurerCpmBannerAllowed && GdBannerMeasurerSystem.DV,
                features.isIntegralAdScienceMeasurerCpmBannerAllowed && GdBannerMeasurerSystem.INTEGRAL_AD_SCIENCE,
                features.isMediascopeMeasurerEnabled && GdBannerMeasurerSystem.MEDIASCOPE,
                features.isMoatMeasurerCpmBannerAllowed && GdBannerMeasurerSystem.MOAT,
                features.isSizmekMeasurerCpmBannerAllowed && GdBannerMeasurerSystem.SIZMEK,
                features.isWeboramaMeasurerCpmBannerAllowed && GdBannerMeasurerSystem.WEBORAMA
            ]);
        case AdvType.CPM_AUDIO:
            return compact([
                features.isAdlooxMeasurerCpmAudioAllowed && GdBannerMeasurerSystem.ADLOOX,
                features.isAdmetricaMeasurerCpmAudioAllowed && GdBannerMeasurerSystem.ADMETRICA,
                features.isAdriverMeasurerCpmAudioAllowed && GdBannerMeasurerSystem.ADRIVER,
                features.isDvMeasurerCpmAudioAllowed && GdBannerMeasurerSystem.DV,
                features.isIntegralAdScienceMeasurerCpmAudioAllowed && GdBannerMeasurerSystem.INTEGRAL_AD_SCIENCE,
                features.isMediascopeMeasurerEnabled && GdBannerMeasurerSystem.MEDIASCOPE,
                features.isMoatMeasurerCpmAudioAllowed && GdBannerMeasurerSystem.MOAT,
                features.isSizmekMeasurerCpmAudioAllowed && GdBannerMeasurerSystem.SIZMEK,
                features.isWeboramaMeasurerCpmAudioAllowed && GdBannerMeasurerSystem.WEBORAMA
            ]);
        case AdvType.CPM_VIDEO:
            return compact([
                features.isAdlooxMeasurerCpmVideoAllowed && GdBannerMeasurerSystem.ADLOOX,
                features.isAdmetricaMeasurerCpmVideoAllowed && GdBannerMeasurerSystem.ADMETRICA,
                features.isAdriverMeasurerCpmVideoAllowed && GdBannerMeasurerSystem.ADRIVER,
                features.isDvMeasurerCpmVideoAllowed && GdBannerMeasurerSystem.DV,
                features.isIntegralAdScienceMeasurerCpmVideoAllowed && GdBannerMeasurerSystem.INTEGRAL_AD_SCIENCE,
                features.isMediascopeMeasurerEnabled && GdBannerMeasurerSystem.MEDIASCOPE,
                features.isMoatMeasurerCpmVideoAllowed && GdBannerMeasurerSystem.MOAT,
                features.isSizmekMeasurerCpmVideoAllowed && GdBannerMeasurerSystem.SIZMEK,
                features.isWeboramaMeasurerCpmVideoAllowed && GdBannerMeasurerSystem.WEBORAMA
            ]);
        case AdvType.CPM_GEOPRODUCT:
            return compact([
                features.isAdlooxMeasurerCpmGeoproductAllowed && GdBannerMeasurerSystem.ADLOOX,
                features.isAdmetricaMeasurerCpmGeoproductAllowed && GdBannerMeasurerSystem.ADMETRICA,
                features.isAdriverMeasurerCpmGeoproductAllowed && GdBannerMeasurerSystem.ADRIVER,
                features.isDvMeasurerCpmGeoproductAllowed && GdBannerMeasurerSystem.DV,
                features.isIntegralAdScienceMeasurerCpmGeoproductAllowed && GdBannerMeasurerSystem.INTEGRAL_AD_SCIENCE,
                features.isMediascopeMeasurerEnabled && GdBannerMeasurerSystem.MEDIASCOPE,
                features.isMoatMeasurerCpmGeoproductAllowed && GdBannerMeasurerSystem.MOAT,
                features.isSizmekMeasurerCpmGeoproductAllowed && GdBannerMeasurerSystem.SIZMEK,
                features.isWeboramaMeasurerCpmGeoproductAllowed && GdBannerMeasurerSystem.WEBORAMA
            ]);
        case AdvType.CPM_DEALS:
            return compact([
                features.isAdlooxMeasurerCpmDealsAllowed && GdBannerMeasurerSystem.ADLOOX,
                features.isAdmetricaMeasurerCpmDealsAllowed && GdBannerMeasurerSystem.ADMETRICA,
                features.isAdriverMeasurerCpmDealsAllowed && GdBannerMeasurerSystem.ADRIVER,
                features.isDvMeasurerCpmDealsAllowed && GdBannerMeasurerSystem.DV,
                features.isIntegralAdScienceMeasurerCpmDealsAllowed && GdBannerMeasurerSystem.INTEGRAL_AD_SCIENCE,
                features.isMediascopeMeasurerEnabled && GdBannerMeasurerSystem.MEDIASCOPE,
                features.isMoatMeasurerCpmDealsAllowed && GdBannerMeasurerSystem.MOAT,
                features.isSizmekMeasurerCpmDealsAllowed && GdBannerMeasurerSystem.SIZMEK,
                features.isWeboramaMeasurerCpmDealsAllowed && GdBannerMeasurerSystem.WEBORAMA
            ]);
        case AdvType.CPM_YNDX_FRONTPAGE:
            return compact([
                features.isAdlooxMeasurerCpmYndxFrontpageAllowed && GdBannerMeasurerSystem.ADLOOX,
                features.isAdmetricaMeasurerCpmYndxFrontpageAllowed && GdBannerMeasurerSystem.ADMETRICA,
                features.isAdriverMeasurerCpmYndxFrontpageAllowed && GdBannerMeasurerSystem.ADRIVER,
                features.isDvMeasurerCpmYndxFrontpageAllowed && GdBannerMeasurerSystem.DV,
                features.isIntegralAdScienceMeasurerCpmYndxFrontpageAllowed &&
                    GdBannerMeasurerSystem.INTEGRAL_AD_SCIENCE,
                features.isMediascopeMeasurerEnabled && GdBannerMeasurerSystem.MEDIASCOPE,
                features.isMoatMeasurerCpmYndxFrontpageAllowed && GdBannerMeasurerSystem.MOAT,
                features.isSizmekMeasurerCpmYndxFrontpageAllowed && GdBannerMeasurerSystem.SIZMEK,
                features.isWeboramaMeasurerCpmYndxFrontpageAllowed && GdBannerMeasurerSystem.WEBORAMA
            ]);
        default:
            return [];
    }
};

export const createSiteLinksByBannersApiData = (sitelinks: EditedBannerSiteLinks): BannerSiteLinksEditorData => {
    return {
        items: sitelinks.items,
        errors: {},
        isDescriptionChecked: true,
        isSaving: false,
        isCanvasOpened: false,
        isUrlChecking: false,
        currentSitelink: 0,
        turboApiUrl: ''
    };
};

export const excludeBannersEditErrorsByBaseInfoChanged = (errors: ValidationErrorData[]): ValidationErrorData[] => {
    return errors.filter(
        error => error.code && !error.code.includes('BannerDefectIds.LanguageDefect.INCONSISTENT_LANGUAGE_WITH_GEO')
    );
};

export const getHelpTextForBannerImageCreative = (bannerType: AdvType | AdvTypeClient): ReactNode => {
    switch (bannerType) {
        case AdvType.MCBANNER:
            return (
                <span>
                    {i18nRaw('creative-help-mc-banner', {
                        requirements: (
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                target={LinkTarget.blank}
                                href={'https://yandex.ru/support/direct/moderation/ad-rules.html#ad-rules__image-ads'}
                            >
                                {i18n('requirements')}
                            </StyledLink>
                        )
                    })}
                </span>
            );
        case AdvType.IMAGE_AD:
        default:
            return (
                <span>
                    {i18nRaw('creative-help', {
                        constructor: (
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                target={LinkTarget.blank}
                                href={'https://yandex.ru/support/direct/products-image-ads/builder.html'}
                            >
                                {i18n('in-constructor')}
                            </StyledLink>
                        ),
                        requirements: (
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                target={LinkTarget.blank}
                                href={'https://yandex.ru/support/direct/moderation/ad-rules.html#ad-rules__image-ads'}
                            >
                                {i18n('requirements')}
                            </StyledLink>
                        )
                    })}
                </span>
            );
    }
};

export function isAddedBannersFromDraftCampaign(
    textAdsMap: Record<BannerId, EditedBannerBase>,
    smartAdsMap: Record<BannerId, EditedBannerBase>,
    mcBannerMap: Record<BannerId, EditedBannerBase>,
    cpmAdsMap: Record<BannerId, EditedBannerBase>
): boolean {
    const all = values(textAdsMap).concat(values(smartAdsMap), values(mcBannerMap), values(cpmAdsMap));
    const uniqBannersByCampaignId = uniqBy(all, (item: EditedBannerBase) => item.campaign.id);

    if (uniqBannersByCampaignId.length !== 1) {
        return false;
    }

    const campaign = uniqBannersByCampaignId[0].campaign;

    return !campaign.hasBanners && campaign.status === GdCampaignPrimaryStatus.DRAFT;
}
